import './chapters.css';

import React, { Component } from 'react';
import lang, { selectedLocalization } from '../services/lang';
import { find } from 'lodash';
import { Link, hashHistory } from 'react-router';
import Icon from '../components/Icon';

import { MEDIA_URI } from '../services/config';
import { chapters, slides, colors, definitions } from '../services/content';
import { beep, ding } from '../services/audio';
import { trackView } from '../services/analytics';

const colorCodes = {
  'blue': "#34A4C1",
  'green': "#8DA036",
  'orange': "#BB7820",
  'purple': "#5b3057",
};


export default class Chapters extends Component {
  constructor(props) {
    super();
    
    const chapterIndex = props.params.chapter;
    const slideIndex = props.params.slide;
    
    const chapter = chapters[chapterIndex];
    const slide = slideIndex 
      ? slides.filter(s => s.chapter === chapter._id)[slideIndex]
      : chapter;
    
    const title = slide['title_'+selectedLocalization] || slide.title;
    const photo = slide.photo;
    const text = slide['text_'+selectedLocalization] || slide.text;
    const css = slide.css;
    const video = slide.video;
    const audio = slide['audio_'+selectedLocalization] || slide.audio;
    
    const chapterColor = find(colors, color => color._id === chapter.color);
    const color = colorCodes[chapterColor.code];
    
    this.state = {
      contentHeight: window.innerHeight - 55,
      contentWidth: window.innerWidth,
      playAudio: props.location.query.audio !== "false",
      audio,
      title,
      photo,
      text,
      css,
      video,
      color,
      isDefinitionActive: false,
      definition: null,
      loop: slide.video_loop_start,
      isDone: false,
    };

    trackView("Learn");
  }
  
  componentDidMount() {
    const { audio, playAudio } = this.state;
    beep();
    
    if(playAudio) {
      this.playAudio(audio);
    }
    
    this.setSize();
    window.addEventListener('resize', this.setSize);
  }
  
  componentWillUnmount() {
    this.stopAudio();
    window.removeEventListener('resize', this.setSize);
  }
  
  toggleAudio = () => {
    const playAudio = !this.state.playAudio;
    if(playAudio) {
      this.playAudio();
    }
    else {
      this.stopAudio();
    }
    
    this.setState({playAudio});
  }
  
  setSize = () => {
    this.setState({
      contentHeight: window.innerHeight - 55,
      contentWidth: window.innerWidth,
    });
  }
  
  next = () => {
    const { playAudio } = this.state;
    let chapterIndex = parseInt(this.props.params.chapter, 10);
    let slideIndex = this.props.params.slide 
      ? parseInt(this.props.params.slide, 10) + 1
      : 0;
    
    const chapter = chapters[chapterIndex];
    const slidesLength = slides.filter(s => s.chapter === chapter._id).length;
    
    if(slideIndex >= slidesLength) {
      slideIndex = "";
      chapterIndex += 1;
    }
    
    if(chapterIndex >= chapters.length) {
      hashHistory.push(`/learn`);
      return;
    }
    
    hashHistory.push(`/chapters/${chapterIndex}/${slideIndex}?audio=${playAudio}`);
  }
  
  prev = () => {
    const { playAudio } = this.state;
    let chapterIndex = parseInt(this.props.params.chapter, 10);
    let slideIndex = this.props.params.slide;
    
    if(!slideIndex && chapterIndex === 0) {
      hashHistory.push(`/learn?audio=${playAudio}`);
      return;
    }
    
    if(!slideIndex && chapterIndex > 0) {
      chapterIndex -= 1;
      slideIndex = slides.filter(s => s.chapter === chapters[chapterIndex]._id).length;
    }
    
    slideIndex = parseInt(slideIndex, 10);
    
    if(slideIndex === 0) {
      hashHistory.push(`/chapters/${chapterIndex}?audio=${playAudio}`);
      return;
    }
    
    hashHistory.push(`/chapters/${chapterIndex}/${slideIndex-1}?audio=${playAudio}`);
  }
  
  setDone = () => {
    ding();
    this.setState({isDone:true});
  }
  
  playAudio() {
    const { audio } = this.state;
    const { path, filename } = audio;
    const url = `${MEDIA_URI}/${path}${filename}`;
    this.audio = new Audio(url);
    this.audio.addEventListener('ended', this.setDone);
    this.audio.play();
  }
  
  stopAudio() {
    if(this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
  }
  
  handleTextClick = e => {
    const definitionId = e.target.dataset.definition;
    if(!definitionId) {
      return;
    }
    const definitionObj = find(definitions, d => d._id === definitionId);
    const definition = {
      title: definitionObj['title_'+selectedLocalization] || definitionObj.title,
      text: definitionObj['text_'+selectedLocalization] || definitionObj.text
    };
    
    this.setState({isDefinitionActive:true, definition});
  }
  
  renderControls() {
    const { playAudio, isDone } = this.state;
    return (
      <div className="chapters-controls">
        <div className="chapters-controls-button" onClick={this.prev}>
          <Icon name="prev" className="chapters-controls-button-icon"/>
        </div>
        <div 
          className="chapters-controls-button" 
          onClick={this.toggleAudio}>
          <Icon 
            name={playAudio ? "audioOff" : "audioOn"} 
            className="chapters-controls-button-icon"
          />
        </div>
        <div className={`chapters-controls-button ${isDone ? 'attract' : ''}`} onClick={this.next}>
          <Icon name="next" className="chapters-controls-button-icon"/>
        </div>
      </div>  
    );
  }
  
  renderVideo() {
    const { video, loop } = this.state;
    if(!video) {
      return null;
    }
    
    const { contentHeight, contentWidth } = this.state;
    const url = MEDIA_URI + '/' + video.path + video.filename;
    
    return (
      <video 
        key={url}
        height={contentHeight}
        width={contentWidth}
        autoPlay={true}
        onEnded={e => {
          if(loop) {
            e.target.currentTime = loop;
            e.target.play();
          }
        }}>
        <source 
          src={url}
          type="video/mp4"
        />
      </video>
    );
  }
  
  renderTitle() {
    const { title, color } = this.state;
    if(!title) {
      return null;
    }
    
    return (
      <div 
        className="chapters-chapter-title" 
        style={{backgroundColor: color}}>
        <div className="chapters-chapter-title-inner">
          {title}
        </div>
      </div>
    );
  }
  
  renderPhoto() {
    const { photo } = this.state;
    if(!photo) {
      return null;
    }
    
    const url = MEDIA_URI + '/' + photo.path + photo.filename;
    
    return (
      <div 
        className="chapters-chapter-photo"
        style={{
          backgroundImage: `url(${url})`
        }}
      />
    );
  }
  
  renderText() {
    const { text, css } = this.state;
    if(!text) {
      return null;
    }
    
    return (
      <div 
        onClick={this.handleTextClick}
        className="chapters-chapter-text" 
        style={{cssText:css}}
        dangerouslySetInnerHTML={{__html:text}}
      />
    );
  }
    
  renderDefinition() {
    const isActive = !!this.state.isDefinitionActive;
    const {title, text} = this.state.definition || "";
    
    return (
      <div 
        className={`chapters-definition ${!!isActive ? 'is-active' : ''}`}
        onClick={() => this.setState({isDefinitionActive:false})}>
        
        <span className='chapters-definition-title'>{title}: </span>
        <span className='chapters-definition-text'>{text}</span>
        
      </div>
    );
  }  
  
  renderContent() {
    if(chapters.length === 0 || slides.length === 0) {
      return <div className="chapters-content"/>;
    }
    
    return (
      <div className="chapters-content">
        {this.renderPhoto()}
        {this.renderVideo()}
        {this.renderTitle()}
        {this.renderText()}
        {this.renderDefinition()}
      </div>
    );
  }
  
  renderFooter() {
    return (
      <div 
        className="chapters-menu-row" 
        style={{backgroundColor:'rgba(255,255,255,0.4)'}}>
        <div className="chapters-menu">
          <Link to="/learn" className="chapters-menu-subtitle">{lang('learn-about-clinical-trials')}</Link>
          <Link to="/menu" className="chapters-menu-main">
            {lang('main-menu')} <Icon name="menuWhite" className="chapters-menu-main-icon"/>
          </Link>
        </div>
      </div>
    );
  }
  
  render() {
    const { color } = this.state;
    return (
      <div 
        className="chapters"
        style={{backgroundColor:color}}>
        {this.renderContent()}
        {this.renderControls()}
        {this.renderFooter()}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/components/Chapters.js